import collection from './collection.js'

const MD_MIN = '(min-width: 768px)'

const collectionFilterPanel = () => {
  const base = collection()

  return {
    ...base,
    panelOpen: false,
    openSections: [],
    stagedChips: [],
    toolbarStuck: false,
    stuckAtOpen: false,
    toolbarHeight: 0,
    _filterTrigger: null,
    _stickyObserver: null,

    formToQueryString(form) {
      if (!form) return ''
      const params = new URLSearchParams()
      const formData = new FormData(form)
      for (const [key, value] of formData) {
        const val = (value || '').toString().trim()
        if (val === '') continue
        if (
          key.startsWith('lsa-dialog-price-') ||
          key.startsWith('lsa-mobile-price-') ||
          key.startsWith('lsa-panel-price-')
        ) {
          continue
        }
        params.append(key, value)
      }
      return params.toString()
    },

    init() {
      base.init.call(this)
      this.filterForm = this.$refs.filterForm || document.querySelector('[data-filter-form]')
      this.filterFormMobile = this.filterForm
      this.openSections = window.matchMedia(MD_MIN).matches ? [0] : []
      this.syncChips()
      this.setupStickyToolbar()
      this.$watch('panelOpen', (open) => {
        if (this.body) {
          this.body.style.overflow = open ? 'hidden' : 'visible'
        }
      })
    },

    setupStickyToolbar() {
      const sticky = this.$el.querySelector('[data-filter-toolbar-sticky]')
      if (!sticky || this._stickyObserver) return

      const sentinel = document.createElement('div')
      sentinel.setAttribute('data-filter-toolbar-sentinel', '')
      sentinel.setAttribute('aria-hidden', 'true')
      sentinel.style.cssText = 'height:1px;width:100%;pointer-events:none;'
      sticky.parentNode.insertBefore(sentinel, sticky)

      const headerHeight =
        parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10) || 0

      this._stickyObserver = new IntersectionObserver(
        ([entry]) => {
          // Scroll lock while the drawer is open breaks sticky + intersection;
          // keep the open-time stuck state until the panel closes.
          if (this.panelOpen) return
          this.toolbarStuck = !entry.isIntersecting
        },
        {
          root: null,
          threshold: 0,
          rootMargin: `-${headerHeight}px 0px 0px 0px`,
        }
      )
      this._stickyObserver.observe(sentinel)
    },

    isToolbarPinned() {
      return this.toolbarStuck || (this.panelOpen && this.stuckAtOpen)
    },

    isMd() {
      return window.matchMedia(MD_MIN).matches
    },

    sortParam() {
      const sortSelector = document.querySelector('#desktop-sort_by[data-sort-by]')
        || document.querySelector('[data-sort-by]')
      let sortValue = 'sort_by'
      if (sortSelector && sortSelector.value) {
        sortValue = sortSelector.value
      }
      this.sort = sortValue
      if (sortValue !== 'manual' && sortValue !== 'sort_by') {
        return 'sort_by=' + sortValue
      }
      return ''
    },

    snapshotForm() {
      const form = this.$refs.filterForm || this.filterForm
      if (!form) {
        this._formSnapshot = null
        return
      }
      this._formSnapshot = {
        checked: [...form.querySelectorAll('input[type="checkbox"], input[type="radio"]')].map((el) => ({
          id: el.id,
          checked: el.checked,
        })),
        priceMin: form.querySelector('input[name="filter.v.price.gte"]')?.value || '',
        priceMax: form.querySelector('input[name="filter.v.price.lte"]')?.value || '',
      }
    },

    restoreFormSnapshot() {
      const form = this.$refs.filterForm || this.filterForm
      const snap = this._formSnapshot
      if (!form) return

      if (!snap) {
        form.reset()
        this.syncOptionUi(form)
        return
      }

      form.querySelectorAll('input[type="checkbox"], input[type="radio"]').forEach((el) => {
        const saved = snap.checked.find((item) => item.id === el.id)
        if (saved) el.checked = saved.checked
      })

      const minInput = form.querySelector('input[name="filter.v.price.gte"]')
      const maxInput = form.querySelector('input[name="filter.v.price.lte"]')
      // Hidden inputs reflect .value onto the content attribute, so form.reset()
      // cannot restore staged price edits — restore from the open-time snapshot.
      if (minInput) minInput.value = snap.priceMin
      if (maxInput) maxInput.value = snap.priceMax

      this.syncOptionUi(form)
    },

    // Keep Alpine selected / selectedPrice in sync with checkbox/radio checked state
    syncOptionUi(form) {
      if (!form) return
      form.querySelectorAll('[x-data]').forEach((el) => {
        try {
          const local = window.Alpine?.$data(el)
          if (local && 'selected' in local) {
            const input = el.querySelector?.('input') || (el.matches?.('label') ? el.querySelector('input') : null)
            const targetInput = input || el.closest('li')?.querySelector('input')
            if (targetInput && targetInput.type !== 'hidden') {
              local.selected = !!targetInput.checked
            }
          }
          if (local && 'selectedPrice' in local) {
            const checked = el.querySelector('input[name^="lsa-panel-price-"]:checked')
            local.selectedPrice = checked ? checked.value : ''
          }
        } catch (_) {
          /* ignore non-alpine nodes */
        }
      })
    },

    openPanel() {
      this._filterTrigger = this.$refs.filterTrigger || document.activeElement
      // Desktop: first group open. Mobile: all collapsed (per LSA001R-38).
      this.openSections = this.isMd() ? [0] : []
      this.snapshotForm()
      this.syncChips()

      // Body scroll-lock unsticks position:sticky — pin with fixed while open.
      this.stuckAtOpen = this.toolbarStuck
      const sticky = this.$el.querySelector('[data-filter-toolbar-sticky]')
      this.toolbarHeight = sticky ? sticky.offsetHeight : 0

      this.panelOpen = true
      this.$nextTick(() => {
        const focusTarget = this.isMd() ? this.$refs.panelClose : this.$refs.mobilePanelClose
        if (focusTarget) focusTarget.focus()
      })
    },

    closePanel({ restoreFocus = true } = {}) {
      this.panelOpen = false
      this.stuckAtOpen = false
      this.toolbarHeight = 0
      if (restoreFocus && this._filterTrigger) {
        this.$nextTick(() => {
          this._filterTrigger.focus()
          this._filterTrigger = null
        })
      }
    },

    toggleSection(index) {
      const i = this.openSections.indexOf(index)
      if (i === -1) {
        this.openSections.push(index)
      } else {
        this.openSections.splice(i, 1)
      }
    },

    isSectionOpen(index) {
      return this.openSections.includes(index)
    },

    syncChips() {
      const form = this.$refs.filterForm || this.filterForm
      if (!form) {
        this.stagedChips = []
        this.hasActiveFilters = false
        return
      }

      const chips = []

      form.querySelectorAll('input[type="checkbox"]:checked, input[type="radio"]:checked').forEach((input) => {
        if (input.name.startsWith('lsa-panel-price-')) return
        if (!input.name.startsWith('filter.')) return

        const labelEl = input.closest('label')
        const label =
          input.dataset.chipLabel ||
          labelEl?.querySelector('[data-option-label]')?.textContent?.trim() ||
          input.getAttribute('aria-label') ||
          input.value

        chips.push({
          id: input.id,
          name: input.name,
          value: input.value,
          label: label,
          type: input.type,
        })
      })

      // Price tiers: show a single chip from hidden min/max when set
      const minInput = form.querySelector('input[name="filter.v.price.gte"]')
      const maxInput = form.querySelector('input[name="filter.v.price.lte"]')
      const minVal = (minInput?.value || '').trim()
      const maxVal = (maxInput?.value || '').trim()
      if (minVal !== '' || maxVal !== '') {
        const selectedRadio = form.querySelector('input[name^="lsa-panel-price-"]:checked')
        const priceLabel =
          selectedRadio?.closest('label')?.querySelector('[data-option-label]')?.textContent?.trim() ||
          (maxVal ? `Price ${minVal || '0'} - ${maxVal}` : `Price ${minVal}+`)
        chips.push({
          id: selectedRadio?.id || 'price-chip',
          name: 'filter.v.price',
          value: `${minVal}-${maxVal}`,
          label: priceLabel,
          type: 'price',
        })
      }

      this.stagedChips = chips
      this.hasActiveFilters = chips.length > 0
    },

    onOptionChange() {
      this.syncChips()
    },

    onPriceChange(event) {
      const input = event.target
      const label = input.closest('label')
      const form = this.$refs.filterForm || this.filterForm
      if (!label || !form) return

      const list = input.closest('[data-min-param]')
      const minName = list?.dataset.minParam
      const maxName = list?.dataset.maxParam
      if (!minName || !maxName) return

      const minInput = form.querySelector(`input[name="${minName}"]`)
      const maxInput = form.querySelector(`input[name="${maxName}"]`)
      if (minInput) minInput.value = label.dataset.priceMin || ''
      if (maxInput) maxInput.value = label.dataset.priceMax || ''

      this.syncChips()
    },

    removeChip(chip) {
      const form = this.$refs.filterForm || this.filterForm
      if (!form) return

      if (chip.type === 'price') {
        form.querySelectorAll('input[name^="lsa-panel-price-"]').forEach((el) => {
          el.checked = false
        })
        const minInput = form.querySelector('input[name="filter.v.price.gte"]')
        const maxInput = form.querySelector('input[name="filter.v.price.lte"]')
        if (minInput) minInput.value = ''
        if (maxInput) maxInput.value = ''
      } else {
        const el = form.querySelector(`#${CSS.escape(chip.id)}`)
        if (el) {
          el.checked = false
        }
      }
      this.syncOptionUi(form)
      this.syncChips()
    },

    clearStaged() {
      const form = this.$refs.filterForm || this.filterForm
      if (!form) return
      form.querySelectorAll('input[type="checkbox"], input[type="radio"]').forEach((el) => {
        el.checked = false
      })
      form.querySelectorAll('input[name="filter.v.price.gte"], input[name="filter.v.price.lte"]').forEach((el) => {
        el.value = ''
      })
      this.syncOptionUi(form)
      this.syncChips()
    },

    applyFilters() {
      const form = this.$refs.filterForm || this.filterForm
      const qs = this.formToQueryString(form)
      const q = [this.qParam, qs, this.sortParam()].filter(Boolean).join('&')
      this.closePanel({ restoreFocus: true })
      this.pushFilter(this.collectionCurrentPath + (q ? '?' + q : ''))
    },

    cancelFilters() {
      this.restoreFormSnapshot()
      this.syncChips()
      this.closePanel({ restoreFocus: true })
    },

    // Override base sort to use md breakpoint and the single form
    sortCollection() {
      this.loading = true
      const form = this.$refs.filterForm || this.filterForm
      const queryString = this.formToQueryString(form)
      const sort = this.sortParam()
      const q = [this.qParam, queryString, sort].filter(Boolean).join('&')
      fetch(this.collectionCurrentPath + (q ? '?' + q : ''))
        .then((response) => response.text())
        .then((data) => {
          const html_div = document.createElement('div')
          html_div.innerHTML = data

          const grid = html_div.querySelector('#ProductGridContainer')
          const currentGrid = document.querySelector('#ProductGridContainer')
          if (grid && currentGrid) currentGrid.innerHTML = grid.innerHTML

          const newCollectionData = html_div.querySelector('[data-collection-json]')
          if (newCollectionData) {
            const parsed = JSON.parse(newCollectionData.textContent)
            this.nextPageUrl = parsed.next_url || null
            this.totalCount = parseInt(parsed.paginate_count, 10) || 0
            this.collectionData = parsed
          }
          this.syncShownCount()
          this.announceResults()

          history.replaceState(null, null, this.collectionCurrentPath + (q ? '?' + q : ''))
        })
        .catch((error) => console.error('Error:', error))
        .finally(() => {
          this.loading = false
        })
    },

    // Keep full filter options after apply (do not swap form HTML); re-sync chips from current form
    pushFilter(newUrl) {
      this.loading = true

      fetch(newUrl)
        .then((response) => response.text())
        .then((data) => {
          const html_div = document.createElement('div')
          html_div.innerHTML = data

          const productGridNew = html_div.querySelector('#ProductGridContainer')
          const productGridCurrent = document.querySelector('#ProductGridContainer')
          if (productGridCurrent && productGridNew) {
            productGridCurrent.innerHTML = productGridNew.innerHTML
          }

          // Do not replace filter form – keep the full option list visible (same as main updateFilter)

          const dataProductDesktopNew = html_div.querySelectorAll('[data-product-desktop]')
          document.querySelectorAll('[data-product-desktop]').forEach((element, index) => {
            if (dataProductDesktopNew[index]) element.innerHTML = dataProductDesktopNew[index].innerHTML
          })

          const dataProductMobileNew = html_div.querySelectorAll('[data-product-mobile]')
          document.querySelectorAll('[data-product-mobile]').forEach((element, index) => {
            if (dataProductMobileNew[index]) element.innerHTML = dataProductMobileNew[index].innerHTML
          })

          const filterBtnLabelNew = html_div.querySelector('[data-filter-button-label]')
          document.querySelectorAll('[data-filter-button-label]').forEach((el) => {
            if (filterBtnLabelNew) el.innerHTML = filterBtnLabelNew.innerHTML
          })

          const newCollectionData = html_div.querySelector('[data-collection-json]')
          if (newCollectionData) {
            const parsed = JSON.parse(newCollectionData.textContent)
            this.nextPageUrl = parsed.next_url || null
            this.totalCount = parseInt(parsed.paginate_count, 10) || 0
            this.collectionData = parsed
          }
          this.syncShownCount()
          this.announceResults()

          history.pushState(null, null, newUrl)

          const newParams = new URL(newUrl, window.location.origin).searchParams
          this.hasActiveFilters = [...newParams.keys()].some((k) => k.startsWith('filter.'))
        })
        .catch((error) => console.error('Error:', error))
        .finally(() => {
          this.filterForm = this.$refs.filterForm || document.querySelector('[data-filter-form]')
          this.filterFormMobile = this.filterForm
          this.syncChips()
          this.query = this.formToQueryString(this.filterForm)
          this.loading = false
        })
    },
  }
}

export default collectionFilterPanel

document.addEventListener('alpine:init', () => {
  Alpine.data('collectionFilterPanel', collectionFilterPanel)
})
